import { useContext, useEffect, useMemo } from 'react';
import { useAbility } from '@casl/react';
import { v4 as uuid } from 'uuid';
import { useTranslate } from '@bynder/localization';

import {
	IconAdd,
	IconAddLink,
	IconAttention,
	IconAutomation,
	IconBarChart,
	IconBoard,
	IconBookmark,
	IconBuilding,
	IconBynder,
	IconCampaign,
	IconCheckbox,
	IconDelete,
	IconEdit,
	IconFile,
	IconFilterFunnel,
	IconFolder,
	IconHome,
	IconImage,
	IconKey,
	IconLink,
	IconListView,
	IconMail,
	IconPages,
	IconQuestion,
	IconRestore,
	IconSettings,
	IconShare,
	IconShield,
	IconTag,
	IconTaxonomy,
	IconTime,
	IconTrendChart,
	IconUser,
	IconUserGroup,
	IconWarning,
} from '@bynder/icons';
import { MenuItem } from '../../types';
import { UUIDToColdfusionID } from '../../helpers/id';
import { useMemoizedSplit } from '../../helpers/useSplit';
import { InfoContext } from '../../context';
import { AbilityContext } from '../../abilities/Can';
import { WFGroupsMenuItem } from '../WorkflowGroupsManagement/WFGroupsMenuItem';

export function useAccountMenuItems(): MenuItem[] {
	const { general, homepage } = useContext(InfoContext);
	const ability = useAbility(AbilityContext);
	const { translate } = useTranslate();
	const bynderExpressSplitEnabled = useMemoizedSplit('portal_bynder_express');
	const aiAgentsSplitEnabled = useMemoizedSplit('ai_agents');
	const isGovernanceSplitEnabled = useMemoizedSplit('analytics_dam_governance');

	const coldfusionID = general.accountId ? UUIDToColdfusionID(general.accountId) : '';

	const canManageHomepage = useMemo(() => {
		return (
			(ability.can('select', 'asmi_manage_homepage') && !!homepage.isAppAccessEnabled) ||
			(ability.can('select', 'asmi_manage_homepage_temp') && !!homepage.isSplitEnabled)
		);
	}, [ability, homepage.isAppAccessEnabled, homepage.isSplitEnabled]);

	useEffect(() => {
		const logToAppcues = async () => {
			const appcuesUser = await window.Appcues?.user();

			if (!appcuesUser) {
				return;
			}

			window.Appcues?.identify(appcuesUser.userId, {
				canEditHomepage: canManageHomepage,
				canOptIntoHomepage: ability.can('select', 'asmi_manage_homepage'),
				hasHomepageConfigured: homepage.isEnabled && homepage.isConfigured,
			});
		};

		logToAppcues();
	}, [ability, homepage.isEnabled, homepage.isConfigured, canManageHomepage]);

	const accountMenuItems: MenuItem[] = useMemo(() => {
		const canShowImprovedTaxonomyMgmt = ability.can('show', 'improved_taxonomy_management');
		const taxonomyUrl = canShowImprovedTaxonomyMgmt ? '/taxonomy/' : '/account/metaproperties/';

		const canShowAdminHomePage =
			canManageHomepage ||
			ability.can('select', 'asmi_look_and_feel') ||
			ability.can('select', 'asmi_news_management') ||
			ability.can('select', 'asmi_general_quicklinks');

		const canShowAdminTaxonomy =
			ability.can('select', 'asmi_manage_brands') ||
			ability.can('select', 'asmi_manage_category') ||
			ability.can('select', 'asmi_manage_metaproperties') ||
			ability.can('select', 'asmi_manage_smartfilters') ||
			ability.can('select', 'asmi_manage_tags');

		const canShowAdminReporting =
			ability.can('select', 'asmi_manage_analytics') ||
			ability.can('select', 'asmi_manage_changehistory');

		const canShowAdminUserRights =
			ability.can('select', 'asmi_manage_user_requests') ||
			ability.can('select', 'asmi_manage_users') ||
			ability.can('select', 'asmi_manage_profiles') ||
			ability.can('select', 'asmi_manage_groups');

		const canShowAdvancedSettings =
			ability.can('select', 'asmi_advaced_settings') ||
			ability.can('select', 'asmi_cockpit') ||
			ability.can('select', 'asmi_trash');

		const canShowAdminBrandTemplates =
			ability.can('select', 'asmi_manage_templates') ||
			ability.can('select', 'asmi_manage_templates_access') ||
			ability.can('select', 'asmi_manage_templates_variables');

		const canShowManageWorkflow = ability.can('select', 'asmi_manage_workflow');

		const canShowQuarantine = ability.can('select', 'quarantine');

		const canSelectWaitingroom = ability.can('select', 'asmi_waitingroom');

		const canShowBynderExpress =
			bynderExpressSplitEnabled && ability.can('select', 'menu_item_transfer');

		const canShowBynderResources = ability.can('select', 'asmi_advaced_settings');

		const canShowCustomLinks = ability.can('show', 'custom_links_overview');

		const canShowAIControlCenter =
			aiAgentsSplitEnabled && ability.can('manage', 'bynder_ai_agents');

		const canShowGovernance =
			isGovernanceSplitEnabled && ability.can('manage', 'bynder_ai_agents');

		const showAccountMenu =
			canShowAdminHomePage ||
			canShowBynderExpress ||
			canSelectWaitingroom ||
			canShowAdminTaxonomy ||
			canShowAdminReporting ||
			canShowAdminUserRights ||
			canShowAdvancedSettings ||
			canShowAdminBrandTemplates ||
			canShowManageWorkflow ||
			canShowQuarantine ||
			canShowCustomLinks ||
			canShowAIControlCenter;

		if (!showAccountMenu) {
			return [];
		}

		const dataItems = [
			// Bynder superadmin
			{
				isVisible: ability.can('superadmin', 'all'),
				icon: IconBynder,
				label: translate('admin.bynderSuperadmin'),
				menuItems: [
					{
						isVisible: true,
						href: '/admin/',
						icon: IconBuilding,
						label: translate('admin.accountManagement'),
					},
					{
						isVisible: true,
						href: `/admin/accountDetail/${coldfusionID}/`,
						icon: IconEdit,
						label: translate('admin.editCurrentAccount'),
					},
				],
			},
			// AI control center
			{
				isVisible: canShowAIControlCenter,
				href: '/ai-control-center/',
				icon: IconAutomation,
				label: translate('admin.aiControlCenter'),
			},
			// Quarantine
			{
				isVisible: ability.can('select', 'quarantine'),
				href: '/quarantine/',
				icon: IconWarning,
				label: translate('quarantine.quarantine'),
			},
			// Waiting room
			{
				isVisible: ability.can('select', 'asmi_waitingroom'),
				href: '/waitingroom/',
				icon: IconTime,
				label: translate('waitingroom.waitingroom'),
			},
			// Bynder Express
			{
				isVisible: canShowBynderExpress,
				href: '/express/',
				icon: IconShare,
				label: translate('menuitem.bynderExpress'),
			},
			// Custom links
			{
				isVisible: canShowCustomLinks,
				href: '/custom-links',
				icon: IconLink,
				label: translate('menuitem.customLinks'),
			},
			// Homepage
			{
				isVisible: canShowAdminHomePage,
				icon: IconHome,
				label: translate('admin.homepage'),
				menuItems: [
					{
						isVisible: canManageHomepage,
						href: '/guidelines/homepages/',
						icon: IconPages,
						label: translate('admin.homepageV2'),
						badgeLabel: translate('badge.label.new'),
					},
					{
						isVisible: ability.can('select', 'asmi_look_and_feel'),
						href: '/account/management/',
						icon: IconImage,
						label: translate('admin.lookAndFeel'),
					},
					{
						isVisible: ability.can('select', 'asmi_news_management'),
						href: '/news/management/',
						icon: IconAttention,
						label: translate('adminExtra.newsManagement'),
					},
					{
						isVisible: ability.can('select', 'asmi_news_management'),
						href: '/news/edit/',
						icon: IconAdd,
						label: translate('news.addNews'),
					},
					{
						isVisible: ability.can('select', 'asmi_general_quicklinks'),
						href: '/pysettings#links',
						icon: IconAddLink,
						label: translate('generalQuickLinks'),
					},
				],
			},
			// Taxonomy
			{
				isVisible: canShowAdminTaxonomy,
				icon: IconTaxonomy,
				label: translate('admin.taxonomy'),
				menuItems: [
					{
						isVisible: ability.can('select', 'asmi_manage_brands'),
						href: '/brands/management/',
						icon: IconFolder,
						label: translate('generalBrands'),
					},
					{
						isVisible: ability.can('select', 'asmi_manage_category'),
						href: '/categories/management/',
						icon: IconBookmark,
						label: translate('generalCategories'),
					},
					{
						isVisible: ability.can('select', 'asmi_manage_metaproperties'),
						href: taxonomyUrl,
						icon: IconUser,
						label: translate('admin.metapropertiesmanagement'),
					},
					{
						isVisible: ability.can('select', 'asmi_manage_smartfilters'),
						href: '/account/smartfilters/',
						icon: IconFilterFunnel,
						label: translate('admin.smartfilterManagement'),
					},
					{
						isVisible: ability.can('select', 'asmi_manage_tags'),
						href: '/tags/management/',
						icon: IconTag,
						label: translate('generalTags'),
					},
				],
			},
			// Admin reporting
			{
				isVisible: canShowAdminReporting,
				icon: IconTrendChart,
				label: translate('admin.reporting'),
				menuItems: [
					{
						isVisible: ability.can('select', 'asmi_manage_analytics'),
						href: '/b-analytics',
						icon: IconBarChart,
						label: translate('admin.analytics'),
					},
					{
						isVisible: canShowGovernance,
						href: '/governance',
						icon: IconShield,
						label: translate('admin.governance'),
					},
					{
						isVisible: ability.can('select', 'asmi_manage_changehistory'),
						href: '/changehistory',
						icon: IconRestore,
						label: translate('generalChangeHistory'),
					},
				],
			},
			// Admin User Rights
			{
				isVisible: canShowAdminUserRights,
				icon: IconUserGroup,
				label: translate('admin.usersRights'),
				menuItems: [
					{
						isVisible:
							ability.can('select', 'asmi_manage_user_requests') &&
							!ability.can('select', 'asmi_manage_user_requests_v2'),
						href: '/user/listrequests/',
						icon: IconUser,
						label: translate('user.requests'),
					},
					{
						isVisible: ability.can('select', 'asmi_manage_user_requests_v2'),
						href: '/pyuser/listrequests/',
						icon: IconUser,
						label: translate('user.requests'),
					},
					{
						isVisible: ability.can('select', 'asmi_manage_users'),
						href: '/user/management/',
						icon: IconUser,
						label: translate('admin.userManagement'),
					},
					{
						isVisible: ability.can('select', 'asmi_manage_profiles'),
						href: '/profiles/management/',
						icon: IconKey,
						label: translate('admin.permissionManagement'),
					},
					{
						isVisible: ability.can('select', 'asmi_manage_groups'),
						href: '/group/management/',
						icon: IconUserGroup,
						label: translate('admin.groupManagement'),
					},
				],
			},
			// Workflow
			{
				isVisible: ability.can('select', 'asmi_manage_workflow'),
				icon: IconCheckbox,
				label: translate('admin.workflow'),
				menuItems: [
					{
						isVisible: ability.can('select', 'asmi_manage_workflow_presets'),
						href: '/workflow/preset/overview/',
						icon: IconSettings,
						label: translate('workflow.PresetManagement'),
					},
					{
						isVisible: ability.can('select', 'asmi_manage_workflow_metaproperties'),
						href: '/workflow/metaproperty/overview/',
						icon: IconListView,
						label: translate('workflow.MetapropertyManagement'),
					},
					{
						isVisible: ability.can('select', 'menu_item_fte_management'),
						href: '/workflow/availability/overview/',
						icon: IconTime,
						label: translate('workflow.FTEManagement'),
					},
					{
						isVisible: ability.can('select', 'asmi_manage_workflow_group'),
						href: '',
						label: '',
						Component: WFGroupsMenuItem,
						componentIsButton: true,
					},
					{
						isVisible: ability.can('select', 'asmi_manage_workflow_group_v2'),
						href: '/pysettings/#workflow/groups/',
						icon: IconUserGroup,
						label: translate('workflow.GroupManagement'),
					},
					{
						isVisible: ability.can('select', 'asmi_manage_workflow_campaign'),
						href: '/workflow/campaign/overview/',
						icon: IconCampaign,
						label: translate('workflow.CampaignManagement'),
					},
					{
						isVisible: ability.can('select', 'asmi_manage_workflow_role'),
						href: '/workflow/account/manageroles/',
						icon: IconKey,
						label: translate('workflow.RoleManagement'),
					},
				],
			},
			// Brand Templates
			{
				isVisible: canShowAdminBrandTemplates,
				icon: IconFile,
				label: translate('admin.brandTemplates'),
				menuItems: [
					{
						isVisible: ability.can('select', 'asmi_manage_templates_access'),
						href: '/settings/templates/#overview',
						icon: IconImage,
						label: translate('workflow.TemplateManagement'),
					},
					{
						isVisible: ability.can('select', 'asmi_manage_templates_variables'),
						href: '/workflow/account/managevariables/',
						icon: IconBuilding,
						label: translate('workflow.VariableManagement'),
					},
				],
			},
			// Bynder Resources
			{
				isVisible: canShowBynderResources,
				icon: IconQuestion,
				label: translate('admin.bynderResources'),
				menuItems: [
					{
						isVisible: true,
						href: 'https://support.bynder.com/',
						icon: IconPages,
						label: translate('admin.knowledgeBase'),
						target: '_blank',
					},
					{
						isVisible: true,
						href: 'https://bynder.nl/supportform/index.php',
						icon: IconMail,
						label: translate('admin.submitRequest'),
						target: '_blank',
					},
					{
						isVisible: true,
						href: 'https://community.bynder.com/',
						icon: IconUserGroup,
						label: translate('admin.community'),
						target: '_blank',
					},
				],
			},
			// Advanced Settings
			{
				isVisible: canShowAdvancedSettings,
				icon: IconSettings,
				label: translate('admin.advancedSettings'),
				menuItems: [
					{
						isVisible: ability.can('select', 'asmi_trash'),
						href: '/trash/',
						icon: IconDelete,
						label: translate('admin.trash'),
					},
					{
						isVisible: ability.can('select', 'asmi_cockpit'),
						href: '/cockpit/',
						icon: IconBoard,
						label: translate('admin.cockpit'),
					},
					{
						isVisible: ability.can('select', 'asmi_advaced_settings'),
						href: '/pysettings/',
						icon: IconBuilding,
						label: translate('admin.portalSettings'),
					},
				],
			},
		];

		return dataItems.map((i0) => ({
			...i0,
			key: uuid(),
			menuItems: i0.menuItems?.map((i1) => ({
				...i1,
				key: uuid(),
			})),
		}));
	}, [
		ability,
		canManageHomepage,
		bynderExpressSplitEnabled,
		aiAgentsSplitEnabled,
		isGovernanceSplitEnabled,
		translate,
		coldfusionID,
	]);

	return accountMenuItems;
}
